/**
 *
 * WidgetListDimensions
 *
 */

import React, { Fragment, useEffect, useRef, useState } from "react"
import { compose } from "redux"
import { connect } from "react-redux"
import { createStructuredSelector } from "reselect"
import PropTypes from "prop-types"

import { Divider, Spin, Tooltip } from "antd"
import WidgetEmptyState from "components/WidgetEmptyState"
import { ExclamationCircleFilled, StarFilled } from "@ant-design/icons"
import { get } from "lodash"
import injectSaga from "utils/injectSaga"
import injectReducer from "utils/injectReducer"

import reducer from "./reducer"
import saga from "./saga"
import "./index.less"
import {
  makeSelectIsLoading,
  makeSelectListDimensionsTop,
  makeSelectListDimensionsBottom,
  makeSelectListDimensionsTopBenchmark,
  makeSelectListDimensionsBottomBenchmark,
} from "./selectors"
import { getListDimensionsWatcher } from "./actions"

import {
  isAdmin,
  isAmbassadorActive,
  isModuleBySlug,
  LOCAL_ANONYMITY,
  LOCAL_ANONYMITY_LEADERS,
} from "../../../../../utils/storage"
import { uuid, validate } from "../../../../../utils/functions"
import { BENCHMARK, TYPE_SURVEY } from "../../../../../utils/constants"
import { loadTexts } from "../../../../../utils/i18n"

function usePreviousMeasureID(value) {
  const ref = useRef()
  useEffect(() => {
    ref.current = value
  })
  return ref.current
}

function usePreviousDivisionID(value) {
  const ref = useRef()
  useEffect(() => {
    ref.current = value
  })
  return ref.current
}

export function WidgetListDimensions(props) {
  const intlWidgetListDimensions = loadTexts("reports_general_view")
    .reportAmbassador.engagementMeasurement.widgetListDimensions
  const [measureID] = useState(props.idMeasure)
  const [divisionID] = useState(props.idDivisionSelected)
  const prevMeasureID = usePreviousMeasureID(measureID)
  const prevDivisionID = usePreviousDivisionID(divisionID)
  useEffect(() => {
    if (prevMeasureID !== measureID) {
      initGetData()
    } else {
      if (prevDivisionID !== divisionID) {
        initGetData()
      }
    }
  }, [props.idMeasure, props.idDivisionSelected])

  const initGetData = async () => {
    const {
      idMeasure,
      getListDimensions,
      idDivisionSelected,
      idDivisionParent,
      selectedSegment,
    } = props
    getListDimensions({
      isHome: true,
      id_measure: idMeasure,
      params: {
        id_measure: idMeasure,
        id_division: idDivisionSelected || idDivisionParent,
        id_segment: selectedSegment,
      },
    })
  }

  const renderBenchmarkTop = () => {
    const { measureGeneral } = props
    if (!measureGeneral.ambassador_benchmark_visible && isAmbassadorActive()) {
      return null
    }
    const surveyType = get(measureGeneral, "id_survey_type", null)
    if (surveyType !== TYPE_SURVEY.climate.idSurveyType) {
      return null
    }
    const { dimensionsTopBenchmark } = props
    const isBenchmark = isModuleBySlug(BENCHMARK)

    if (!isBenchmark) {
      return null
    }

    if (
      !validate.exist(dimensionsTopBenchmark) ||
      dimensionsTopBenchmark.length === 0
    ) {
      return (
        <div className="containerDimensionTop--benchmark-empty">
          {intlWidgetListDimensions.benchmarkTop}
        </div>
      )
    }

    let listDimensions = ""
    dimensionsTopBenchmark.forEach((element, index) => {
      if (dimensionsTopBenchmark.length === 1) {
        return (listDimensions = `${element}.`)
      }

      if (index === 0) {
        listDimensions = listDimensions + element
      } else if (index === dimensionsTopBenchmark.length - 1) {
        listDimensions = `${listDimensions} y ${element}.`
      } else {
        listDimensions = `${listDimensions}, ${element}`
      }
    })

    return (
      <div className="containerDimensionTop--benchmark">
        <div className="containerDimensionTop--benchmark-title">
          {`${intlWidgetListDimensions.benchmarkTopHave}`}
          <span className="containerDimensionTop--benchmark-title-count">
            {dimensionsTopBenchmark.length}{" "}
            {dimensionsTopBenchmark.length === 1 ? "dimensión" : "dimensiones"}
          </span>{" "}
          {intlWidgetListDimensions.benchmarkAbove}
        </div>
        <div className="containerDimensionTop--benchmark-list">
          <Tooltip placement="topRight" title={listDimensions}>
            {listDimensions}
          </Tooltip>
        </div>
      </div>
    )
  }

  const renderListDimensionTop = () => {
    const { dimensionsTop, measureGeneral } = props
    const anonymityCount = isAdmin()
      ? LOCAL_ANONYMITY()
      : LOCAL_ANONYMITY_LEADERS()
    const anonymityValidate =
      get(measureGeneral, "employees", 0) < parseInt(anonymityCount)
    if (
      anonymityValidate ||
      !validate.exist(dimensionsTop) ||
      dimensionsTop.length === 0
    ) {
      return (
        <WidgetEmptyState
          description={intlWidgetListDimensions.emptyStateTopDescription}
        />
      )
    }
    return dimensionsTop.map((row, index) => {
      return (
        <li key={uuid()} className="containerDimensionTop--list-item">
          <div className="containerDimensionTop--list-item-name">
            <span>{index + 1}.</span>&nbsp;&nbsp;
            <span>{row.dimension_name}</span>
          </div>
          <span className="containerDimensionTop--list-item-value">
            {row.value_visible}
          </span>
        </li>
      )
    })
  }

  const renderDimensionsTop = () => {
    return (
      <div className="containerDimensionTop">
        <div className="containerDimensionTop--title">
          <StarFilled style={{ fontSize: "14px", color: "#FBD668" }} />{" "}
          <span>{intlWidgetListDimensions.titleDimensionsTop}</span>
        </div>
        <ol className="containerDimensionTop--list">
          {renderListDimensionTop()}
        </ol>
        {renderBenchmarkTop()}
      </div>
    )
  }

  const renderListDimensionBottom = () => {
    const { dimensionsBottom, measureGeneral } = props
    const anonymityCount = isAdmin()
      ? LOCAL_ANONYMITY()
      : LOCAL_ANONYMITY_LEADERS()
    const anonymityValidate =
      get(measureGeneral, "employees", 0) < parseInt(anonymityCount)
    if (
      anonymityValidate ||
      !validate.exist(dimensionsBottom) ||
      dimensionsBottom.length === 0
    ) {
      return (
        <WidgetEmptyState
          description={intlWidgetListDimensions.emptyStateBottomDescription}
        />
      )
    }
    return dimensionsBottom.map((row, index) => {
      return (
        <li key={uuid()} className="containerDimensionBottom--list-item">
          <div className="containerDimensionBottom--list-item-name">
            <span>{index + 1}.</span>&nbsp;&nbsp;
            <span>{row.dimension_name}</span>
          </div>
          <span className="containerDimensionBottom--list-item-value">
            {row.value_visible}
          </span>
        </li>
      )
    })
  }

  const renderBenchmarkBottom = () => {
    const { measureGeneral } = props
    if (!measureGeneral.ambassador_benchmark_visible && isAmbassadorActive()) {
      return null
    }
    const surveyType = get(measureGeneral, "id_survey_type", null)
    if (surveyType !== TYPE_SURVEY.climate.idSurveyType) {
      return null
    }
    const { dimensionsBottomBenchmark } = props
    const isBenchmark = isModuleBySlug(BENCHMARK)

    if (!isBenchmark) {
      return null
    }

    if (
      !validate.exist(dimensionsBottomBenchmark) ||
      dimensionsBottomBenchmark.length === 0
    ) {
      return (
        <div className="containerDimensionBottom--benchmark-empty">
          {intlWidgetListDimensions.emptyBenchmarkDimensionBottom}
        </div>
      )
    }

    let listDimensions = ""
    dimensionsBottomBenchmark.forEach((element, index) => {
      if (dimensionsBottomBenchmark.length === 1) {
        return (listDimensions = `${element}.`)
      }

      if (index === 0) {
        listDimensions = listDimensions + element
      } else if (index === dimensionsBottomBenchmark.length - 1) {
        listDimensions = `${listDimensions} y ${element}.`
      } else {
        listDimensions = `${listDimensions}, ${element}`
      }
    })

    return (
      <div className="containerDimensionBottom--benchmark">
        <div className="containerDimensionBottom--benchmark-title">
          {`${intlWidgetListDimensions.benchmarkBottomHave} `}
          <span className="containerDimensionBottom--benchmark-title-count">
            {dimensionsBottomBenchmark.length}{" "}
            {dimensionsBottomBenchmark.length === 1
              ? "dimensión"
              : "dimensiones"}
          </span>{" "}
          {intlWidgetListDimensions.benchmarkBottomBelow}
        </div>
        <div className="containerDimensionBottom--benchmark-list">
          <Tooltip placement="topRight" title={listDimensions}>
            {listDimensions}
          </Tooltip>
        </div>
      </div>
    )
  }

  const renderDimensionsBottom = () => {
    return (
      <div className="containerDimensionBottom">
        <div className="containerDimensionBottom--title">
          <ExclamationCircleFilled
            style={{ fontSize: "14px", color: "#E37879" }}
          />{" "}
          <span>{intlWidgetListDimensions.titleDimensionsBottom}</span>
        </div>
        <ol className="containerDimensionBottom--list">
          {renderListDimensionBottom()}
        </ol>
        {renderBenchmarkBottom()}
      </div>
    )
  }

  if (props.isLoading) {
    return (
      <div className="WidgetListDimensions boxShadow">
        <div className="loadingSpin">
          <Spin size="large" />
        </div>
      </div>
    )
  }

  return (
    <div className="WidgetListDimensions boxShadow">
      {renderDimensionsTop()}
      <Divider className="dividerDimensions" type="vertical" />
      {renderDimensionsBottom()}
    </div>
  )
}
WidgetListDimensions.propTypes = {
  isLoading: PropTypes.bool,
  measureGeneral: PropTypes.object,
  dimensionsTop: PropTypes.array,
  dimensionsBottom: PropTypes.array,
  dimensionsTopBenchmark: PropTypes.array,
  dimensionsBottomBenchmark: PropTypes.array,
  idMeasure: PropTypes.number,
  idDivisionSelected: PropTypes.number,
  idDivisionParent: PropTypes.number,
  selectedSegment: PropTypes.number,
}

const mapStateToProps = createStructuredSelector({
  isLoading: makeSelectIsLoading(),
  dimensionsTop: makeSelectListDimensionsTop(),
  dimensionsBottom: makeSelectListDimensionsBottom(),
  dimensionsTopBenchmark: makeSelectListDimensionsTopBenchmark(),
  dimensionsBottomBenchmark: makeSelectListDimensionsBottomBenchmark(),
})

function mapDispatchToProps(dispatch) {
  return {
    getListDimensions: (params) => dispatch(getListDimensionsWatcher(params)),
  }
}
const withConnect = connect(mapStateToProps, mapDispatchToProps)
const withReducer = injectReducer({ key: "widgetListDimensions", reducer })
const withSaga = injectSaga({ key: "widgetListDimensions", saga })

export default compose(withReducer, withSaga, withConnect)(WidgetListDimensions)
